import React, { useState, useEffect } from 'react';
import '../style/campaignLocation.css';

const CampaignLocation = ({ 
  locationHierarchy = [], 
  selectedLocations = [], 
  onSelectionChange,
  searchTerm = "",
  onSearchChange 
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [localSelectedLocations, setLocalSelectedLocations] = useState(selectedLocations);

  useEffect(() => {
    setLocalSelectedLocations(selectedLocations);
  }, [selectedLocations]);

  const getLevelColor = (level) => {
    const colors = {
      1: '#006d7d',
      2: '#20b2aa',
      3: '#808080',
      4: '#a9a9a9'
    };
    return colors[level] || '#d3d3d3';
  };

  const toggleExpanded = (locationId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(locationId)) {
      newExpanded.delete(locationId);
    } else {
      newExpanded.add(locationId);
    }
    setExpandedItems(newExpanded);
  };

  const getAllChildrenIds = (location) => {
    let childrenIds = [];
    if (location.children && location.children.length > 0) {
      location.children.forEach(child => {
        childrenIds.push(child.id);
        childrenIds = childrenIds.concat(getAllChildrenIds(child));
      });
    }
    return childrenIds;
  };

  const getAllParentIds = (locationId, locations) => {
    let parentIds = [];
    const findParent = (items, targetId, currentPath = []) => {
      for (let item of items) {
        const newPath = [...currentPath, item.id];
        if (item.id === targetId) {
          return currentPath;
        }
        if (item.children && item.children.length > 0) {
          const result = findParent(item.children, targetId, newPath);
          if (result) return result;
        }
      }
      return null;
    };
    return findParent(locations, locationId) || [];
  };

  const handleLocationSelect = (location, isSelected) => {
    let newSelected = [...localSelectedLocations];
    let newExpanded = new Set(expandedItems);
    
    if (isSelected) {

      if (!newSelected.includes(location.id)) {
        newSelected.push(location.id);
      }
      

      const childrenIds = getAllChildrenIds(location);
      childrenIds.forEach(childId => {
        if (!newSelected.includes(childId)) {
          newSelected.push(childId);
        }
      });

    
      if (location.children && location.children.length > 0) {
        newExpanded.add(location.id);

        const parentIds = getAllParentIds(location.id, locationHierarchy);
        parentIds.forEach(parentId => {
          newExpanded.add(parentId);
        });
      }
    } else {
     
      newSelected = newSelected.filter(id => id !== location.id);
    
      const childrenIds = getAllChildrenIds(location);
      newSelected = newSelected.filter(id => !childrenIds.includes(id));
    }

    setLocalSelectedLocations(newSelected);
    setExpandedItems(newExpanded);
    onSelectionChange(newSelected);
  };

  const isLocationSelected = (locationId) => {
    const isSelected = localSelectedLocations.includes(locationId);
    return isSelected;
  };

  const isLocationIndeterminate = (location) => {
    if (!location.children || location.children.length === 0) {
      return false;
    }

    if (localSelectedLocations.includes(location.id)) {
      return false;
    }
    
    const hasSelectedChildren = location.children.some(child => 
      localSelectedLocations.includes(child.id) || isLocationIndeterminate(child)
    );
    
    const allChildrenSelected = location.children.every(child => 
      localSelectedLocations.includes(child.id)
    );
    
    return hasSelectedChildren && !allChildrenSelected;
  };

  const filterLocations = (locations, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return locations;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    const filterRecursive = (items) => {
      return items.reduce((filtered, location) => {
        const matchesSearch = location.name.toLowerCase().includes(searchLower);
        

        if (matchesSearch) {
          filtered.push({
            ...location,
            children: [] 
          });
        }
        
     
        if (location.children && location.children.length > 0) {
          const matchingChildren = filterRecursive(location.children);
          filtered.push(...matchingChildren);
        }
        
        return filtered;
      }, []);
    };
    
    return filterRecursive(locations);
  };

  const renderLocationItem = (location, depth = 0) => {
    const hasChildren = location.children && location.children.length > 0;
    const isExpanded = expandedItems.has(location.id);
    const isSelected = isLocationSelected(location.id);
    const isIndeterminate = isLocationIndeterminate(location);

    console.log('Rendering location:', {
      id: location.id,
      name: location.name,
      level: location.level,
      hasChildren,
      isExpanded,
      isSelected,
      isIndeterminate
    });


    return (
      <div key={location.id} className="location-selector-item">
        <div 
          className={`location-selector-row ${hasChildren ? 'expandable' : ''} ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${depth * 20}px` }}
          onClick={hasChildren ? (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleExpanded(location.id);
          } : undefined}
        >
          {hasChildren && (
            <div 
              className="expand-icon"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(location.id);
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </div>
          )}
          {!hasChildren && <div className="expand-spacer" />}
          
          <div 
            className="level-indicator"
            style={{ backgroundColor: getLevelColor(location.level) }}
          />
          
          <div 
            className="checkbox-container"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLocationSelect(location, !isSelected);
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              ref={(el) => {
                if (el) {
                  el.indeterminate = isIndeterminate;
                }
              }}
              onChange={(e) => {
                e.stopPropagation();
                handleLocationSelect(location, e.target.checked);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="location-checkbox"
            />
          </div>
          
          <span className="location-name">{location.name}</span>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="location-children">
            {location.children.map(child => renderLocationItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredLocations = filterLocations(locationHierarchy, searchTerm);


  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      const expandAllForSearch = (locations) => {
        const newExpanded = new Set(expandedItems);
        locations.forEach(location => {
          if (location.children && location.children.length > 0) {
            newExpanded.add(location.id);
            expandAllForSearch(location.children);
          }
        });
        setExpandedItems(newExpanded);
      };
      expandAllForSearch(filteredLocations);
    } else {
  
      setExpandedItems(new Set());
    }
  }, [searchTerm, filteredLocations]);


  return (
    <div className="location-hierarchy-selector">
      <div className="location-tree-container">
        {filteredLocations.length > 0 ? (
          filteredLocations.map(location => renderLocationItem(location))
        ) : (
          <div className="no-locations-message">
            {searchTerm ? 'No locations found matching your search.' : 'No locations available.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignLocation;
